import { useState, useEffect, useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { motion } from 'framer-motion';
import {
  Link2, Search, Sparkles, Loader2, ExternalLink, Mail, TrendingUp,
  Globe, Target, ArrowUpRight, CheckCircle2, XCircle, MessageSquare,
  FileText, Wand2, Copy, Check, Send, ShieldCheck, RefreshCw, Radar,
  Clock, ChevronDown, ChevronUp, Trash2, History,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { api } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { useTaskState } from '../../context/TaskStateContext';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  prospected: { label: 'Prospected', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  negotiating: { label: 'Negotiating', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  acquired: { label: 'Acquired', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  lost: { label: 'Lost', color: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' },
};
const DIFF_COLOR = { easy: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', hard: 'bg-red-100 text-red-700' };
const DIST_COLOR = { draft: 'bg-slate-100 text-slate-700', submitted: 'bg-blue-100 text-blue-700', published: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700' };

function DABadge({ da }) {
  const c = da >= 50 ? 'text-emerald-600 bg-emerald-50' : da >= 30 ? 'text-blue-600 bg-blue-50' : 'text-amber-600 bg-amber-50';
  return <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full ${c}`}>Authority {da}</span>;
}

function isLiveVerified(opp) {
  return Boolean(opp?.verified && opp?.verification?.link_found);
}

function LinkRelBadge({ verification }) {
  if (!verification?.link_found) return null;

  const label = verification.is_sponsored
    ? 'Sponsored'
    : verification.is_ugc
      ? 'UGC'
      : verification.is_nofollow
        ? 'Nofollow'
        : verification.is_dofollow
          ? 'Dofollow'
          : 'Qualified';
  const color = verification.is_dofollow
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';

  return (
    <Badge className={`${color} gap-1`}>
      <ShieldCheck className="h-3 w-3" />{label}
    </Badge>
  );
}

export default function BacklinksPage() {
  const { selectedProjectId } = useProject();
  const [opportunities, setOpportunities] = useState([]);
  const [oppTotal, setOppTotal] = useState(0);
  const [oppHasMore, setOppHasMore] = useState(false);
  const [acquired, setAcquired] = useState([]);
  const [acqTotal, setAcqTotal] = useState(0);
  const [acqHasMore, setAcqHasMore] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [researching, setResearching] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [researchWebsite, setResearchWebsite] = useState('');
  const [researchIndustry, setResearchIndustry] = useState('');
  const [showResearchDialog, setShowResearchDialog] = useState(false);
  const [outreachDialog, setOutreachDialog] = useState(null);
  const [outreachOppId, setOutreachOppId] = useState(null);
  const [outreachContactEmail, setOutreachContactEmail] = useState('');
  const [outreachSubject, setOutreachSubject] = useState('');
  const [outreachBody, setOutreachBody] = useState('');
  const [outreachFollowupBody, setOutreachFollowupBody] = useState('');
  const outreachTask = useTaskState('backlinks-outreach');
  const generatingOutreach = outreachTask.running;
  const [discovering, setDiscovering] = useState(false);
  const [refreshingPR, setRefreshingPR] = useState(false);
  const [researchMode, setResearchMode] = useState('real');
  const [targetInfo, setTargetInfo] = useState(null);
  const [targetOptions, setTargetOptions] = useState([]);
  const [selectedTargetSource, setSelectedTargetSource] = useState('manual');

  // Content posting state
  const [distributions, setDistributions] = useState([]);
  const [selectedDist, setSelectedDist] = useState(null);
  const [postLoading, setPostLoading] = useState(false);
  const [showPostDialog, setShowPostDialog] = useState(false);
  const [postOpp, setPostOpp] = useState(null);
  const [postWebsite, setPostWebsite] = useState('');
  const [postIndustry, setPostIndustry] = useState('');
  const [postWordCount, setPostWordCount] = useState(800);
  const [copied, setCopied] = useState(false);
  const [expandedTimeline, setExpandedTimeline] = useState(null);
  const [oppPage, setOppPage] = useState(1);
  const [acqPage, setAcqPage] = useState(1);
  const [clearing, setClearing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [discoveryRunsOpen, setDiscoveryRunsOpen] = useState(false);
  const [discoveryRuns, setDiscoveryRuns] = useState([]);
  const [discoveryRunsLoading, setDiscoveryRunsLoading] = useState(false);
  const ITEMS_PER_PAGE = 10;

  const fetchData = useCallback(async () => {
    try {
      const [oppRes, acqRes, statsRes, distRes, onboardRes, targetRes] = await Promise.all([
        api.get(`/backlinks/opportunities?page=${oppPage}&page_size=${ITEMS_PER_PAGE}${statusFilter ? `&status=${statusFilter}` : ''}`),
        api.get(`/backlinks/acquired?page=${acqPage}&page_size=${ITEMS_PER_PAGE}`),
        api.get('/backlinks/stats'),
        api.get('/content-distribution'),
        api.get('/onboarding').catch(() => ({ data: {} })),
        api.get('/backlinks/target').catch(() => ({ data: null })),
      ]);
      setOpportunities(oppRes.data.items || []);
      setOppTotal(oppRes.data.total || 0);
      setOppHasMore(oppRes.data.has_more || false);
      setAcquired(acqRes.data.items || []);
      setAcqTotal(acqRes.data.total || 0);
      setAcqHasMore(acqRes.data.has_more || false);
      setStats(statsRes.data);
      setDistributions(distRes.data.items || distRes.data.distributions || []);
      const backlinkTarget = targetRes.data || {};
      const persistedOptions = backlinkTarget.options || [];
      const mergedOptions = persistedOptions.filter(
        (opt, idx, arr) => opt.website && arr.findIndex(other => other.website === opt.website) === idx
      );
      setTargetInfo(backlinkTarget);
      setTargetOptions(mergedOptions);
      if (backlinkTarget.selected_website || mergedOptions[0]?.website) {
        const selected = backlinkTarget.selected_website || mergedOptions[0].website;
        setResearchWebsite(current => current || selected);
        setSelectedTargetSource(backlinkTarget.selected_source || mergedOptions[0]?.source || 'manual');
      }
      // Pre-fill research dialog from onboarding
      const goals = onboardRes.data?.goals || {};
      if (goals.website) setResearchWebsite(current => current || goals.website);
      if (goals.industry) setResearchIndustry(current => current || goals.industry);
    } catch (err) {
      toast.error('Failed to load backlink data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, oppPage, acqPage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll every 15s while a background discovery is running; refresh data when it finishes
  useEffect(() => {
    if (!stats?.is_running) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get('/backlinks/stats');
        if (!res.data.is_running) {
          clearInterval(interval);
          fetchData();
        } else {
          setStats(res.data);
        }
      } catch (_) {}
    }, 15000);
    return () => clearInterval(interval);
  }, [stats?.is_running, fetchData]);

  const persistSelectedTarget = async () => {
    const source = selectedTargetSource || 'manual';
    const option = targetOptions.find(opt => opt.website === researchWebsite && opt.source === source);
    const websiteToSave = option?.raw_value || researchWebsite;
    if (source === 'gsc') {
      await api.post('/analytics/gsc/select-site', { site_url: websiteToSave }).catch(() => null);
    }
    await api.post('/backlinks/target', { website: websiteToSave, source });
  };

  const handleViewDiscoveryHistory = async () => {
    setDiscoveryRunsOpen(true);
    setDiscoveryRunsLoading(true);
    try {
      const res = await api.get('/backlinks/discovery-runs', { params: { page: 1, page_size: 20 } });
      setDiscoveryRuns(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load discovery history');
    } finally {
      setDiscoveryRunsLoading(false);
    }
  };

  const handleResearch = async () => {
    if (!researchWebsite) { toast.error('Please enter your website URL'); return; }
    try {
      await persistSelectedTarget();
    } catch (err) {
      // Manual discovery can still proceed with the supplied website.
    }
    if (researchMode === 'real') {
      return handleDiscover();
    }
    setResearching(true);
    try {
      const res = await api.post('/backlinks/research', { website: researchWebsite, industry: researchIndustry || null, count: 15, project_id: selectedProjectId || undefined });
      toast.success(res.data.message);
      setShowResearchDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Research failed'); }
    finally { setResearching(false); }
  };

  const handleDiscover = async () => {
    if (!researchWebsite) { toast.error('Please enter your website URL'); return; }
    try {
      await persistSelectedTarget();
    } catch (err) {
      // Discovery request includes the website, so a failed preference save is non-blocking.
    }
    setDiscovering(true);
    try {
      const res = await api.post('/backlinks/discover', {
        project_id: selectedProjectId || undefined,
        website: researchWebsite,
        use_web_search: true,
        verify: true,
      });
      const d = res.data;
      if ((d.total_discovered || 0) > 0) {
        toast.success(`Found ${d.total_discovered} real backlinks (${d.verified_count || 0} verified)`);
      } else {
        toast.info('Discovery finished with no new backlinks yet', {
          description: 'No matching pages were found this time. Try another connected site or run discovery again later.',
        });
      }
      setShowResearchDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Discovery failed'); }
    finally { setDiscovering(false); }
  };

  const handleRefreshPageRank = async () => {
    setRefreshingPR(true);
    try {
      const res = await api.post('/backlinks/pagerank');
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error('Failed to refresh PageRank scores'); }
    finally { setRefreshingPR(false); }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Are you sure you want to clean up rejected/lost opportunities?')) return;
    try {
      const res = await api.delete('/backlinks/cleanup');
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error('Cleanup failed'); }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      const res = await api.delete('/backlinks/all');
      const { deleted_opportunities, deleted_acquired, deleted_distributions } = res.data;
      toast.success(
        `Cleared ${deleted_opportunities} opportunities, ${deleted_acquired} acquired links, ${deleted_distributions} guest posts.`,
      );
      setOppPage(1);
      setAcqPage(1);
      setSelectedDist(null);
      setOutreachDialog(null);
      setOutreachOppId(null);
      setShowClearConfirm(false);
      fetchData();
    } catch {
      toast.error('Failed to clear backlink data');
    } finally {
      setClearing(false);
    }
  };

  const handleVerify = async (oppId) => {
    try {
      const res = await api.post(`/backlinks/verify/${oppId}`);
      const v = res.data.verification;
      if (v?.link_found) {
        toast.success(`Link verified: ${v.is_dofollow ? 'Dofollow' : 'Nofollow'}`);
      } else {
        toast.warning('Link not found on the page');
      }
      fetchData();
    } catch (err) { toast.error('Verification failed'); }
  };

  const handleStatusUpdate = async (id, status) => {
    try { await api.put(`/backlinks/opportunities/${id}/status${selectedProjectId ? `?project_id=${selectedProjectId}` : ''}`, { status }); toast.success(`Status updated to ${status}`); fetchData(); }
    catch { toast.error('Failed to update status'); }
  };

  const handleGenerateOutreach = async (id, contactEmail = '') => {
    outreachTask.start();
    try {
      const res = await api.post(`/backlinks/opportunities/${id}/outreach`);
      setOutreachDialog(res.data);
      setOutreachOppId(id);
      setOutreachContactEmail(contactEmail || '');
      setOutreachSubject(res.data.subject_line || '');
      setOutreachBody(res.data.email_body || '');
      setOutreachFollowupBody(res.data.follow_up_body || '');
      toast.success('Outreach content generated');
      outreachTask.complete();
      fetchData();
    }
    catch (err) {
      toast.error('Failed to generate outreach');
      outreachTask.fail(err);
    }
  };

  const handleSendOutreach = async () => {
    if (!outreachOppId) return;
    if (!outreachContactEmail.trim()) {
      toast.error('Please enter a contact email before sending.');
      return;
    }
    setSendingEmail(true);
    try {
      await api.patch(`/backlinks/opportunities/${outreachOppId}`, { contact_email: outreachContactEmail.trim() });
      await api.post(`/backlinks/opportunities/${outreachOppId}/send-outreach`, {
        subject: outreachSubject.trim(),
        body: outreachBody.trim(),
      });
      toast.success('Outreach email sent!');
      setOutreachDialog(null);
      setOutreachOppId(null);
      setOutreachContactEmail('');
      setOutreachSubject('');
      setOutreachBody('');
      setOutreachFollowupBody('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendFollowup = async () => {
    if (!outreachOppId) return;
    setSendingEmail(true);
    try {
      await api.post(`/backlinks/opportunities/${outreachOppId}/send-followup`, { approved: true });
      toast.success('Follow-up email sent!');
      setOutreachDialog(null);
      setOutreachOppId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send follow-up');
    } finally {
      setSendingEmail(false);
    }
  };

  const openPostDialog = (opp) => { setPostOpp(opp); setShowPostDialog(true); };

  const handleCreatePost = async () => {
    if (!postWebsite.trim()) { toast.error('Enter your website URL'); return; }
    setPostLoading(true);
    try {
      const res = await api.post('/content-distribution/guest-post', {
        opportunity_id: postOpp.opportunity_id, client_website: postWebsite,
        client_industry: postIndustry, word_count: postWordCount, tone: 'professional',
      });
      setSelectedDist(res.data.distribution);
      setShowPostDialog(false);
      toast.success('Guest post generated!');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    finally { setPostLoading(false); }
  };

  const updateDistStatus = async (id, status) => {
    try { await api.put(`/content-distribution/${id}/status`, { status }); toast.success(`Marked as ${status}`); fetchData(); }
    catch { toast.error('Update failed'); }
  };

  const copyText = (text) => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const filtered = opportunities.filter(o =>
    !searchTerm || o.domain?.toLowerCase().includes(searchTerm.toLowerCase()) || o.content_angle?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const hasOpportunitySearch = Boolean(searchTerm.trim() || statusFilter);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Backlinks</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Real backlink discovery, verification, outreach & content posting</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowClearConfirm(true)}
            disabled={clearing || (oppTotal === 0 && acqTotal === 0 && distributions.length === 0)}
            className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 dark:border-red-900"
            title="Delete all opportunities, acquired backlinks, and guest-post drafts"
          >
            {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Clear All
          </Button>
          <Button variant="outline" size="sm" onClick={handleCleanup} title="Remove self-referencing entries">
            <XCircle className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={handleRefreshPageRank} disabled={refreshingPR} className="gap-2" title="Refresh real PageRank scores">
            {refreshingPR ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} PageRank
          </Button>
          <Button variant="outline" onClick={handleViewDiscoveryHistory} title="View past discovery runs" className="gap-2">
            <History className="h-4 w-4" /> Discovery History
          </Button>
          <Button onClick={() => setShowResearchDialog(true)} className="gap-2"><Radar className="h-4 w-4" /> Discover Backlinks</Button>
        </div>
      </div>

      <Card className="border-indigo-100 dark:border-indigo-900/40">
        <CardContent className="py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Backlink target: {targetInfo?.selected_website || researchWebsite || 'Not selected'}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {targetInfo?.selected_source === 'gsc'
                ? 'Using the selected Google Search Console site. Daily discovery runs automatically for eligible plans.'
                : targetInfo?.selected_website
                  ? 'Using your saved account website. Connect GSC for the most reliable verified target.'
                  : 'Connect/select a GSC site or enter a website before discovery.'}
            </p>
          </div>
          <div className="text-xs text-slate-500">
            Daily automation: {targetInfo?.daily_automation?.enabled ? 'Enabled' : 'Waiting for website'}
            {targetInfo?.daily_automation?.last_run_date ? ` · Last run ${targetInfo.daily_automation.last_run_date}` : ''}
          </div>
        </CardContent>
      </Card>

      {stats?.is_running && (
        <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          <span>Backlink discovery is running in the background — results will appear automatically when it finishes.</span>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card><CardContent className="pt-6 text-center"><div className="text-3xl font-bold">{stats.total}</div><div className="text-sm text-slate-500 mt-1">Opportunities</div></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><div className="text-3xl font-bold text-emerald-600">{stats.by_status?.acquired || 0}</div><div className="text-sm text-slate-500 mt-1">Acquired</div></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><div className="text-3xl font-bold text-blue-600">{stats.by_status?.contacted || 0}</div><div className="text-sm text-slate-500 mt-1">In Outreach</div></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><div className="text-3xl font-bold text-indigo-600">{stats.avg_da || 0}</div><div className="text-sm text-slate-500 mt-1">Avg Authority</div></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><div className="text-3xl font-bold text-violet-600">{distributions.length}</div><div className="text-sm text-slate-500 mt-1">Content Posts</div></CardContent></Card>
        </div>
      )}

      <Tabs defaultValue="opportunities">
        <TabsList>
          <TabsTrigger value="opportunities">Opportunities ({opportunities.length})</TabsTrigger>
          <TabsTrigger value="posting">Content Posting ({distributions.length})</TabsTrigger>
          <TabsTrigger value="acquired">Acquired ({acquired.length})</TabsTrigger>
        </TabsList>

        {/* === OPPORTUNITIES TAB === */}
        <TabsContent value="opportunities" className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search by domain or topic..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setOppPage(1); }} className="pl-10" />
            </div>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setOppPage(1); }} className="px-3 py-2 border rounded-md bg-white dark:bg-slate-900 dark:border-slate-700 text-sm">
              <option value="">All Statuses</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="space-y-3">
            {filtered.length === 0 ? (
              hasOpportunitySearch ? (
                <Card><CardContent className="py-12 text-center text-slate-500"><Search className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>No backlinks found for this search.</p></CardContent></Card>
              ) : (
                <Card><CardContent className="py-12 text-center text-slate-500"><Globe className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>No opportunities yet. Click "Discover Backlinks" to start.</p></CardContent></Card>
              )
            ) : filtered.map(opp => (
              <Card key={opp.opportunity_id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <a href={opp.url || `https://${opp.domain}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-indigo-600 hover:underline flex items-center gap-1 truncate">
                          {opp.verification?.anchor_text || opp.title || opp.domain} <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </a>
                        {(opp.verification?.anchor_text || opp.title) && (
                          <span className="text-xs text-slate-500 truncate">{opp.domain}</span>
                        )}
                        <DABadge da={opp.estimated_da || 0} />
                        <Badge className={STATUS_CONFIG[opp.status]?.color || ''}>{STATUS_CONFIG[opp.status]?.label || opp.status}</Badge>
                        <Badge className={DIFF_COLOR[opp.difficulty] || ''}>{opp.difficulty}</Badge>
                        {opp.verified && opp.verification?.link_found && <LinkRelBadge verification={opp.verification} />}
                        {opp.verified && !opp.verification?.link_found && (
                          <Badge className="bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 gap-1">
                            <CheckCircle2 className="h-3 w-3" />URL Live
                          </Badge>
                        )}
                        {!opp.verified && (
                          <Badge className="bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 gap-1">
                            <Clock className="h-3 w-3" />Unverified
                          </Badge>
                        )}
                        {opp.discovered_via && (
                          <Badge variant="outline" className="text-xs">
                            {opp.discovered_via === 'web_search' ? 'Real Discovery' : opp.discovered_via === 'ai_research' ? 'AI Research' : opp.discovered_via}
                          </Badge>
                        )}
                        {opp.pagerank_score > 0 && <span className="text-xs text-slate-400">PR: {opp.pagerank_score}</span>}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{opp.content_angle}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1"><Target className="h-3 w-3" />{opp.opportunity_type?.replace('_', ' ')}</span>
                        <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" />Relevance: {opp.relevance_score}/10</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => handleVerify(opp.opportunity_id)} title="Verify backlink is live">
                        <ShieldCheck className="h-3 w-3" />
                      </Button>
                      {opp.status === 'prospected' && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => handleGenerateOutreach(opp.opportunity_id, opp.contact_email)} disabled={generatingOutreach} title="Generate outreach email">
                            {generatingOutreach ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(opp.opportunity_id, 'contacted')} title="Mark contacted">
                            <ArrowUpRight className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                      {opp.status === 'contacted' && (
                        <Button
                          size="sm"
                          onClick={() => handleStatusUpdate(opp.opportunity_id, 'acquired')}
                          disabled={!isLiveVerified(opp)}
                          title={isLiveVerified(opp) ? 'Mark acquired' : 'Verify the live backlink before marking acquired'}
                          className="gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <CheckCircle2 className="h-3 w-3" /> Acquired
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openPostDialog(opp)} title="Write guest post">
                        <FileText className="h-3 w-3" />
                      </Button>
                      {opp.outreach_content && (
                        <Button size="sm" variant="ghost" onClick={() => { setOutreachDialog(opp.outreach_content); setOutreachOppId(opp.opportunity_id); setOutreachContactEmail(opp.contact_email || ''); setOutreachSubject(opp.outreach_content?.subject_line || ''); setOutreachBody(opp.outreach_content?.email_body || ''); setOutreachFollowupBody(opp.outreach_content?.follow_up_body || ''); }} title="View outreach">
                          <MessageSquare className="h-3 w-3" />
                        </Button>
                      )}
                      {opp.activity?.length > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => setExpandedTimeline(expandedTimeline === opp.opportunity_id ? null : opp.opportunity_id)} title="Activity timeline">
                          <Clock className="h-3 w-3 mr-1" />
                          <span className="text-xs">{opp.activity.length}</span>
                          {expandedTimeline === opp.opportunity_id ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                        </Button>
                      )}
                    </div>
                  </div>
                  {expandedTimeline === opp.opportunity_id && opp.activity?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                      <h4 className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><Clock className="h-3 w-3" /> Activity Timeline</h4>
                      <div className="space-y-2 ml-2 border-l-2 border-slate-200 dark:border-slate-700 pl-3">
                        {[...opp.activity].reverse().map((act, idx) => (
                          <div key={idx} className="relative">
                            <div className="absolute -left-[19px] top-1 w-2.5 h-2.5 rounded-full bg-white dark:bg-slate-900 border-2 border-indigo-400" />
                            <div className="text-xs">
                              <span className="font-medium text-slate-700 dark:text-slate-300">{act.detail}</span>
                              <div className="flex items-center gap-2 mt-0.5 text-slate-400">
                                <span>by {act.actor}</span>
                                <span>&middot;</span>
                                <span>{new Date(act.at).toLocaleDateString()} {new Date(act.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          {oppTotal > 0 && (
            <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Showing {((oppPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(oppPage * ITEMS_PER_PAGE, oppTotal)} of {oppTotal}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={oppPage <= 1} onClick={() => setOppPage(p => Math.max(1, p - 1))}>Previous</Button>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Page {oppPage} of {Math.ceil(oppTotal / ITEMS_PER_PAGE)}
                </span>
                <Button variant="outline" size="sm" disabled={!oppHasMore} onClick={() => setOppPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* === CONTENT POSTING TAB === */}
        <TabsContent value="posting" className="space-y-4">
          {distributions.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No content posts yet. Go to Opportunities and click the document icon on any site to generate a guest post.</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-4">
              {selectedDist && (
                <Card className="border-violet-200 dark:border-violet-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{selectedDist.title}</CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge className={DIST_COLOR[selectedDist.status] || 'bg-slate-100'}>{selectedDist.status}</Badge>
                        <Button size="sm" variant="ghost" onClick={() => setSelectedDist(null)}>Collapse</Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2 flex-wrap text-xs text-slate-500">
                      <span>Target: <strong>{selectedDist.target_domain}</strong></span>
                      <span>Words: {selectedDist.word_count}</span>
                      <span>Read: {selectedDist.estimated_read_time}</span>
                    </div>
                    <p className="text-xs italic text-slate-500">{selectedDist.meta_description}</p>
                    <div className="flex gap-1 flex-wrap">
                      {(selectedDist.target_keywords || []).map((kw, i) => <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>)}
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-sm max-h-80 overflow-y-auto prose prose-sm dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedDist.article_body) }}
                    />
                    {selectedDist.author_bio && (
                      <div className="bg-violet-50 dark:bg-violet-950/30 rounded-lg p-3 text-xs"><strong>Author Bio:</strong> {selectedDist.author_bio}</div>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" onClick={() => copyText(selectedDist.article_body)}>
                        {copied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}{copied ? 'Copied' : 'Copy Article'}
                      </Button>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => updateDistStatus(selectedDist.distribution_id, 'submitted')}>
                        <Send className="h-3 w-3 mr-1" /> Submitted
                      </Button>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => updateDistStatus(selectedDist.distribution_id, 'published')}>
                        Published
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {distributions.map((d, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900 transition" onClick={() => setSelectedDist(d)}>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm truncate">{d.title}</h4>
                    <p className="text-xs text-slate-400">{d.target_domain} &middot; {d.word_count} words &middot; {new Date(d.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge className={DIST_COLOR[d.status] || 'bg-slate-100'}>{d.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* === ACQUIRED TAB === */}
        <TabsContent value="acquired" className="space-y-3">
          {acquired.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-slate-500"><Link2 className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>No acquired backlinks yet.</p></CardContent></Card>
          ) : acquired.map((bl, i) => (
            <Card key={i}>
              <CardContent className="py-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    <Globe className="h-4 w-4 text-emerald-500" />
                    {bl.verification?.anchor_text || bl.title || bl.domain}
                    <DABadge da={bl.estimated_da || 0} />
                  </div>
                  {(bl.verification?.anchor_text || bl.title) && (
                    <p className="text-xs text-slate-500 mt-0.5">{bl.domain}</p>
                  )}
                  <div className="text-sm text-slate-500 mt-1">{bl.opportunity_type?.replace('_', ' ')} &bull; Acquired {bl.acquired_at?.split('T')[0]}</div>
                </div>
                <a href={bl.url || `https://${bl.domain}`} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="ghost"><ExternalLink className="h-4 w-4" /></Button></a>
              </CardContent>
            </Card>
          ))}
          {acqTotal > 0 && (
            <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Showing {((acqPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(acqPage * ITEMS_PER_PAGE, acqTotal)} of {acqTotal}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={acqPage <= 1} onClick={() => setAcqPage(p => Math.max(1, p - 1))}>Previous</Button>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Page {acqPage} of {Math.ceil(acqTotal / ITEMS_PER_PAGE)}
                </span>
                <Button variant="outline" size="sm" disabled={!acqHasMore} onClick={() => setAcqPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Research Dialog */}
      <Dialog open={showResearchDialog} onOpenChange={setShowResearchDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Radar className="h-5 w-5 text-indigo-500" /> Backlink Discovery</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <button
                onClick={() => setResearchMode('real')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${researchMode === 'real' ? 'bg-white dark:bg-slate-700 shadow text-indigo-600 dark:text-indigo-400' : 'text-slate-500'}`}
              >
                <Radar className="h-4 w-4 inline mr-1" /> Real Data
              </button>
              <button
                onClick={() => setResearchMode('ai')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${researchMode === 'ai' ? 'bg-white dark:bg-slate-700 shadow text-violet-600 dark:text-violet-400' : 'text-slate-500'}`}
              >
                <Sparkles className="h-4 w-4 inline mr-1" /> AI Research
              </button>
            </div>
            {researchMode === 'real' ? (
              <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded-lg p-3 text-xs text-emerald-700 dark:text-emerald-400">
                Searches the live web to find <strong>real pages that already link</strong> to your domain. Verifies each link exists and checks dofollow/nofollow status with real PageRank scores.
              </div>
            ) : (
              <div className="bg-violet-50 dark:bg-violet-950/20 rounded-lg p-3 text-xs text-violet-700 dark:text-violet-400">
                Uses AI-powered web search to find <strong>real websites</strong> in your industry that accept guest posts, have resource pages, or run directories — sites where you could <strong>earn new backlinks</strong> through outreach. All URLs are verified from live search results.
              </div>
            )}
            {targetOptions.length > 0 && (
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Connected Website</label>
                <select
                  value={researchWebsite}
                  onChange={e => {
                    const option = targetOptions.find(opt => opt.website === e.target.value);
                    setResearchWebsite(e.target.value);
                    setSelectedTargetSource(option?.source || 'manual');
                  }}
                  className="mt-1 w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-900 dark:border-slate-700 text-sm"
                >
                  {targetOptions.map(option => (
                    <option key={`${option.source}-${option.website}`} value={option.website}>
                      {option.label}: {option.website}{option.recommended ? ' (recommended)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  GSC is preferred because it is a verified site property. GA4 stays connected for analytics context.
                </p>
              </div>
            )}
            <div><label className="text-sm font-medium text-slate-700 dark:text-slate-300">Your Website URL</label><Input placeholder="https://example.com" value={researchWebsite} onChange={e => { setResearchWebsite(e.target.value); setSelectedTargetSource('manual'); }} className="mt-1" /></div>
            {researchMode === 'ai' && (
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Industry <span className="text-slate-400 font-normal">(auto-detected from your website if blank)</span>
                </label>
                <Input placeholder="e.g. dog grooming, dental clinic, SaaS marketing" value={researchIndustry} onChange={e => setResearchIndustry(e.target.value)} className="mt-1" />
              </div>
            )}
            <Button onClick={handleResearch} disabled={researching || discovering} className="w-full gap-2">
              {(researching || discovering) ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> {researchMode === 'real' ? 'Discovering real backlinks...' : 'Researching...'}</>
              ) : researchMode === 'real' ? (
                <><Radar className="h-4 w-4" /> Discover Real Backlinks</>
              ) : (
                <><Sparkles className="h-4 w-4" /> Find AI Opportunities</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Outreach Dialog */}
      <Dialog open={!!outreachDialog} onOpenChange={() => { setOutreachDialog(null); setOutreachOppId(null); setOutreachContactEmail(''); setOutreachSubject(''); setOutreachBody(''); setOutreachFollowupBody(''); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Outreach Content</DialogTitle></DialogHeader>
          {outreachDialog && (
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-slate-500">Contact Email <span className="text-red-500">*</span></label>
                <Input
                  type="email"
                  placeholder="contact@example.com"
                  value={outreachContactEmail}
                  onChange={e => setOutreachContactEmail(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Subject Line</label>
                <input
                  type="text"
                  value={outreachSubject}
                  onChange={e => setOutreachSubject(e.target.value)}
                  className="mt-1 w-full p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm font-medium border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Email Body</label>
                <textarea
                  value={outreachBody}
                  onChange={e => setOutreachBody(e.target.value)}
                  rows={10}
                  className="mt-1 w-full p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
                />
              </div>
              {outreachFollowupBody && (
                <div>
                  <label className="text-sm font-medium text-slate-500">Follow-Up</label>
                  <textarea
                    value={outreachFollowupBody}
                    onChange={e => setOutreachFollowupBody(e.target.value)}
                    rows={8}
                    className="mt-1 w-full p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
                  />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleSendOutreach} disabled={sendingEmail || !outreachContactEmail.trim()}>
                  {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Outreach Email
                </Button>
                {outreachDialog.follow_up_body && (
                  <Button variant="outline" className="flex-1" onClick={handleSendFollowup} disabled={sendingEmail}>
                    <Mail className="w-4 h-4 mr-2" />Send Follow-up
                  </Button>
                )}
              </div>
              <Button variant="ghost" className="w-full text-slate-500" onClick={() => { setOutreachDialog(null); setOutreachOppId(null); setOutreachContactEmail(''); setOutreachSubject(''); setOutreachBody(''); setOutreachFollowupBody(''); }}>Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Guest Post Dialog */}
      <Dialog open={showPostDialog} onOpenChange={setShowPostDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-violet-500" /> Write Guest Post for {postOpp?.domain}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-xs"><strong>Content Angle:</strong> {postOpp?.content_angle}</div>
            <div><label className="text-sm font-medium">Your Website URL</label><Input placeholder="https://yoursite.com" value={postWebsite} onChange={e => setPostWebsite(e.target.value)} className="mt-1" /></div>
            <div><label className="text-sm font-medium">Industry</label><Input placeholder="e.g. digital marketing" value={postIndustry} onChange={e => setPostIndustry(e.target.value)} className="mt-1" /></div>
            <div><label className="text-sm font-medium">Word Count</label><Input type="number" min={300} max={3000} value={postWordCount} onChange={e => setPostWordCount(Number(e.target.value))} className="mt-1" /></div>
            <Button onClick={handleCreatePost} disabled={postLoading} className="w-full gap-2 bg-violet-600 hover:bg-violet-700 text-white">
              {postLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</> : <><Wand2 className="h-4 w-4" /> Generate Guest Post</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Discovery History Dialog */}
      <Dialog open={discoveryRunsOpen} onOpenChange={setDiscoveryRunsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-500" /> Discovery History
            </DialogTitle>
          </DialogHeader>
          {discoveryRunsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
            </div>
          ) : discoveryRuns.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No discovery runs yet. Click "Discover Backlinks" to start.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
              {discoveryRuns.map((run) => (
                <div key={run.run_id} className="py-3 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm text-slate-800 dark:text-slate-200 truncate">
                      {run.inputs?.target_website}
                    </span>
                    <span className="text-xs text-slate-400 flex-shrink-0">
                      {new Date(run.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="text-indigo-600 font-medium">{run.outputs?.total_discovered || 0} discovered</span>
                    <span>·</span>
                    <span className="text-emerald-600 font-medium">{run.outputs?.verified_count || 0} verified</span>
                    {run.duration_ms && (
                      <>
                        <span>·</span>
                        <span>{(run.duration_ms / 1000).toFixed(1)}s</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all backlink data</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes all backlink opportunities, acquired backlinks, and guest-post drafts for your account.
              Run discovery again to rebuild your list. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearAll} className="bg-red-600 hover:bg-red-700">
              {clearing ? 'Clearing…' : 'Clear All'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
